import { useQueryClient } from '@tanstack/react-query'
import { UserListResponse } from 'src/models/user.models'
import { orgQueryKeys } from 'src/queries/queries.constants'
import {
  ExtendBaseQueryOptions,
  MutationMethod,
  useExtendMutation,
  useExtendQuery,
} from 'src/queries/queries.utils'

export interface GetPeopleManagerRequest extends ExtendBaseQueryOptions {
  organizationId: string | undefined
  userId: string | undefined
}

interface PutPeopleManagerBody {
  managerId?: string
}

export interface PutPeopleManagerRequest {
  organizationId: string
  userId: string
  body: PutPeopleManagerBody
}

interface ManagerAssignment {
  organizationId: string
  userId: string
  manager?: UserListResponse
}

export interface PeopleManagerResponse {
  managerAssignment: ManagerAssignment
}

export const useQueryPeopleManager = ({
  organizationId,
  userId,
  enabled = true,
}: GetPeopleManagerRequest) => {
  return useExtendQuery<PeopleManagerResponse>({
    queryKey: orgQueryKeys.peopleManagers(organizationId, userId),
    queryUrl: () =>
      `/organizations/${organizationId}/members/${userId}/managerassignment`,
    enabled: enabled && organizationId !== undefined && userId !== undefined,
  })
}

export const usePutPeopleManager = () => {
  const queryClient = useQueryClient()
  return useExtendMutation<PeopleManagerResponse, PutPeopleManagerRequest>(
    MutationMethod.PUT,
    {
      mutationUrl: ({ organizationId, userId }) =>
        `/organizations/${organizationId}/members/${userId}/managerassignment`,
      onSuccess: ({ managerAssignment }) => {
        queryClient.invalidateQueries({
          queryKey: orgQueryKeys.peopleManagers(
            managerAssignment.organizationId,
            managerAssignment.userId,
          ),
        })
      },
    },
  )
}

export interface PeopleManagerDirectReportsResponse {
  // list of user IDs
  directReports: string[]
}

export const useQueryPeopleManagerDirectReports = ({
  organizationId,
  userId,
  enabled = true,
}: GetPeopleManagerRequest) => {
  return useExtendQuery({
    queryKey: orgQueryKeys.peopleManagerDirectReports(organizationId, userId),
    queryUrl: () =>
      `/organizations/${organizationId}/members/${userId}/directreports`,
    enabled: enabled && organizationId !== undefined && userId !== undefined,
    select: (data: PeopleManagerDirectReportsResponse): string[] =>
      data.directReports || [],
  })
}

export interface GetPeopleManagerCountRequest extends ExtendBaseQueryOptions {
  organizationId: string | undefined
}

export const useQueryPeopleManagersCount = ({
  organizationId,
  enabled = true,
}: GetPeopleManagerCountRequest) => {
  return useExtendQuery({
    queryKey: orgQueryKeys.peopleManagerCount(organizationId),
    queryUrl: () => `/organizations/${organizationId}/managers/count`,
    enabled: enabled && organizationId !== undefined,
    select: (data: { count: number }): number => data.count || 0,
  })
}
